'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { find, replace } from 'lodash';
import compactFP from 'lodash/fp/compact';
import flowFP from 'lodash/fp/flow';
import mapFP from 'lodash/fp/map';
import orderByFP from 'lodash/fp/orderBy';
import Button from '@/components/shared/Button';
import type { Action, Contact } from '@/graphql/common/types';
import { Select } from '@axa/design-system';

type SidebarProps = {
  handleClearSearch: () => void;
  title?: string;
  description?: string;
  contact?: Contact;
  action?: Action;
  insurances?: {
    title: string;
    slug: string;
  }[];
};

type Option = {
  label: string;
  value: string | number;
};

export const Sidebar = ({
  handleClearSearch,
  title,
  description,
  contact,
  action,
  insurances
}: SidebarProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const type = searchParams.get('tipo');

  const [selectedInsurance, setSelectedInsurance] = useState<Option | undefined>();

  const insuranceOptions = useMemo(() => {
    if (!insurances) return [];

    const options = flowFP(
      mapFP((insurance: { title?: string; slug?: string }) => {
        if (insurance) {
          return {
            label: insurance?.title,
            value: insurance?.slug
          };
        }

        return null;
      }),
      compactFP,
      orderByFP(['label'], ['asc'])
    )(insurances) as Option[];

    const insurance = find(options, { value: type });

    if (insurance) {
      setSelectedInsurance(insurance as Option);
    }

    return options;
  }, [insurances, type]);

  const handleChangeType = (value?: Option) => {
    setSelectedInsurance(value);

    if (value?.value) {
      router.push(`/condicoes-gerais?tipo=${value.value}`);
      handleClearSearch();
    } else {
      router.push('/condicoes-gerais');
      router.refresh();
    }
  };

  return (
    <aside className="flex size-full flex-col justify-between gap-4 bg-ocean-300">
      <div className="flex flex-col gap-4 px-6 py-10 lg:pb-2 lg:pt-6 2xl:p-12">
        <h1 className="font-publico text-2xl font-bold text-white 2xl:text-[2rem] 2xl:leading-10">
          {title || 'Condições gerais'}
        </h1>
        {description && <p className="text-base font-regular text-white">{description}</p>}
        <Select
          id="general-condition-type"
          className="pt-2 2xl:pt-6"
          placeholder="Selecione a classe de Seguros"
          options={insuranceOptions}
          searchable={false}
          onChange={handleChangeType}
          value={selectedInsurance}
        />
      </div>
      <div className="hidden px-6 py-10 lg:flex lg:flex-col lg:gap-6 lg:pb-6 lg:pt-2 2xl:p-12">
        {contact && (
          <div className="flex flex-col">
            <h4 className="pb-2 text-sm font-regular leading-[1.125rem] tracking-[1px] text-white">
              {contact.name}
            </h4>
            {contact.phone && (
              <p className="text-base font-semibold text-white">
                Telefone:{' '}
                <a href={`tel:${replace(contact.phone, /\s/g, '')}`} className="hover:underline">
                  {contact.phone}
                </a>
              </p>
            )}
            {contact.email && (
              <p className="text-base font-semibold text-white">
                E-mail:{' '}
                <a href={`mailto:${contact.email}`} className="hover:underline">
                  {contact.email}
                </a>
              </p>
            )}
          </div>
        )}
        {action?.link && (
          <Button variant="light" outline={action.outline || true} className="w-fit" asChild>
            <Link
              href={action.link.url}
              target={action.link.target && `_${action.link.target}`}
              aria-label={action.link.ariaLabel}
            >
              {action.link.label}
            </Link>
          </Button>
        )}
      </div>
    </aside>
  );
};
